import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BookOpen,
  ChevronDown,
  Clock,
  GraduationCap,
  Minus,
  Target,
  Plus,
  X,
} from "lucide-react";
import { getApiBaseUrl } from "../config/api";
import { downloadApiFile } from "../utils/downloadFile";

const API_URL = getApiBaseUrl();

const ACTIVITY_TYPES = [
  { value: "course", label: "Course" },
  { value: "training", label: "Training" },
  { value: "workshop", label: "Workshop" },
  { value: "certification", label: "Certification" },
  { value: "self_study", label: "Self-study" },
  { value: "other", label: "Other" },
];

const STATUSES = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "on_hold", label: "On Hold" },
];

const emptyActivity = () => ({
  id: null,
  title: "",
  description: "",
  activity_type: "course",
  provider: "",
  status: "not_started",
  progress_pct: 0,
  priority: "medium",
  target_date: "",
  hours_planned: 6,
  in_plan: true,
  notes: "",
});

function activityHours(a) {
  const logged = Math.max(0, Number(a.hours_logged) || 0);
  const planned = Math.max(0, Number(a.hours_planned) || 0);
  let pct =
    planned > 0
      ? Math.min(100, Math.round((logged / planned) * 100))
      : Math.max(0, Math.min(100, Number(a.progress_pct) || 0));
  if (a.status === "completed") pct = 100;
  return { logged, planned, pct };
}

function barColor(status, pct) {
  if (status === "completed" || pct >= 100) return "#22c55e";
  if (status === "not_started" && pct === 0) return "#e5e7eb";
  if (status === "on_hold") return "#f59e0b";
  return "#3b82f6";
}

function statusStyle(status) {
  if (status === "completed") return { color: "#16a34a", fontWeight: 600 };
  if (status === "in_progress") return { color: "#111827", fontWeight: 500 };
  if (status === "on_hold") return { color: "#c2410c", fontWeight: 500 };
  return { color: "#6b7280", fontWeight: 500 };
}

function ProgressTrack({ pct, color, height = 10 }) {
  const value = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div
      style={{
        height,
        background: "#e5e7eb",
        borderRadius: 999,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${value}%`,
          height: "100%",
          background: color,
          borderRadius: 999,
          transition: "width 0.3s ease",
        }}
      />
    </div>
  );
}

/**
 * My Learning Journey — personal learner portal dashboard.
 */
export default function MyLearningDashboard({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState({
    summary: {},
    activities: [],
    goals: [],
    hours: [],
    milestones: [],
    certificates: [],
    reflections: [],
    reminders: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyActivity());
  const [showMore, setShowMore] = useState(false);
  const [moreTab, setMoreTab] = useState("reminders");
  const [logModal, setLogModal] = useState(null); // { activity, notes, hours }

  // Secondary forms
  const [goalHours, setGoalHours] = useState(10);
  const [goalPeriod, setGoalPeriod] = useState("weekly");
  const [milestoneTitle, setMilestoneTitle] = useState("");
  const [reflectionText, setReflectionText] = useState("");
  const [certTitle, setCertTitle] = useState("");
  const [certFile, setCertFile] = useState(null);
  const [certFileKey, setCertFileKey] = useState(0);
  const [certBusy, setCertBusy] = useState(false);
  const [recs, setRecs] = useState([]);
  const [recsNote, setRecsNote] = useState("");

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API_URL}/get_my_learning.php?user_id=${user.id}`
      );
      if (!res.data?.success) {
        setError(res.data?.message || "Could not load learning journey.");
        return;
      }
      setData({
        summary: res.data.summary || {},
        activities: res.data.activities || [],
        goals: res.data.goals || [],
        hours: res.data.hours || [],
        milestones: res.data.milestones || [],
        certificates: res.data.certificates || [],
        reflections: res.data.reflections || [],
        reminders: res.data.reminders || [],
      });
    } catch (err) {
      setError(err?.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  };

  const totals = useMemo(() => {
    const activities = data.activities || [];
    let logged = 0;
    let planned = 0;
    activities.forEach((a) => {
      const h = activityHours(a);
      logged += h.logged;
      planned += h.planned > 0 ? h.planned : 0;
    });
    // Prefer active goal target for overall hours goal when set
    const activeGoal = (data.goals || []).find((g) => g.status === "active");
    const goalTarget = activeGoal
      ? Number(activeGoal.target_hours) || 0
      : planned;
    const hoursDone = activeGoal
      ? Number(activeGoal.hours_done) || logged
      : logged;
    const denom = goalTarget > 0 ? goalTarget : planned > 0 ? planned : 0;
    const overallPct =
      denom > 0
        ? Math.min(100, Math.round((hoursDone / denom) * 100))
        : activities.length
          ? Math.round(
              activities.reduce((s, a) => s + activityHours(a).pct, 0) /
                activities.length
            )
          : 0;
    return {
      count: activities.length,
      hoursDone: Math.round(hoursDone * 10) / 10,
      hoursGoal: Math.round(denom * 10) / 10,
      overallPct,
    };
  }, [data.activities, data.goals]);

  const syncProgressAfterHours = async (activity, newLogged) => {
    const planned = Number(activity.hours_planned) || 0;
    let pct =
      planned > 0
        ? Math.min(100, Math.round((newLogged / planned) * 100))
        : activity.progress_pct;
    let status = activity.status;
    if (pct >= 100) {
      pct = 100;
      status = "completed";
    } else if (newLogged > 0 && status === "not_started") {
      status = "in_progress";
    } else if (newLogged > 0 && status === "on_hold") {
      status = "in_progress";
    } else if (newLogged <= 0 && status !== "completed") {
      status = "not_started";
      pct = 0;
    }
    await axios.post(`${API_URL}/save_learning_activity.php`, {
      user_id: user.id,
      id: activity.id,
      title: activity.title,
      description: activity.description,
      activity_type: activity.activity_type,
      provider: activity.provider,
      status,
      progress_pct: pct,
      priority: activity.priority,
      target_date: activity.target_date || null,
      hours_planned: activity.hours_planned,
      in_plan: activity.in_plan ? 1 : 0,
      notes: activity.notes,
    });
  };

  const openLogModal = (activity) => {
    setLogModal({
      activity,
      notes: "",
      hours: 1,
    });
  };

  const submitLogHours = async (e) => {
    e?.preventDefault?.();
    if (!logModal?.activity) return;
    const activity = logModal.activity;
    const hours = Math.max(0.25, Math.min(24, Number(logModal.hours) || 1));
    const notes = String(logModal.notes || "").trim();
    setBusyId(activity.id);
    try {
      const res = await axios.post(`${API_URL}/log_learning_hours.php`, {
        user_id: user.id,
        activity_id: activity.id,
        hours,
        logged_date: new Date().toISOString().slice(0, 10),
        notes: notes || null,
      });
      if (!res.data?.success) {
        alert(res.data?.message || "Could not log time.");
        return;
      }
      const next = (Number(activity.hours_logged) || 0) + hours;
      await syncProgressAfterHours(activity, next);
      setLogModal(null);
      await load();
      flash(
        notes
          ? `+${hours}h logged with notes`
          : `+${hours} hour${hours === 1 ? "" : "s"} logged`
      );
    } catch (err) {
      alert(err?.message || "Network error.");
    } finally {
      setBusyId(null);
    }
  };

  const removeOneHour = async (activity) => {
    const logged = Number(activity.hours_logged) || 0;
    if (logged <= 0) return;
    setBusyId(activity.id);
    try {
      const entry = (data.hours || []).find(
        (h) => h.activity_id === activity.id && Number(h.hours) > 0
      );
      if (entry) {
        await axios.post(`${API_URL}/delete_learning_item.php`, {
          user_id: user.id,
          type: "hours",
          id: entry.id,
        });
        const removed = Number(entry.hours) || 1;
        await syncProgressAfterHours(
          activity,
          Math.max(0, logged - removed)
        );
      }
      await load();
      flash("Hour removed");
    } catch (err) {
      alert(err?.message || "Network error.");
    } finally {
      setBusyId(null);
    }
  };

  const saveActivity = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusyId("form");
    try {
      const planned = Number(form.hours_planned) || 0;
      let status = form.status;
      let pct = Number(form.progress_pct) || 0;
      if (form.id) {
        const existing = (data.activities || []).find((a) => a.id === form.id);
        if (existing && planned > 0) {
          pct = Math.min(
            100,
            Math.round(((Number(existing.hours_logged) || 0) / planned) * 100)
          );
        }
      }
      const res = await axios.post(`${API_URL}/save_learning_activity.php`, {
        user_id: user.id,
        id: form.id || undefined,
        title: form.title.trim(),
        description: form.description,
        activity_type: form.activity_type,
        provider: form.provider,
        status,
        progress_pct: pct,
        priority: form.priority || "medium",
        target_date: form.target_date || null,
        hours_planned: planned,
        in_plan: form.in_plan ? 1 : 0,
        notes: form.notes,
      });
      if (!res.data?.success) {
        alert(res.data?.message || "Could not save.");
        return;
      }
      setShowForm(false);
      setForm(emptyActivity());
      await load();
      flash(form.id ? "Updated" : "Learning added");
    } catch (err) {
      alert(err?.message || "Network error.");
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (a) => {
    setForm({
      id: a.id,
      title: a.title || "",
      description: a.description || "",
      activity_type: a.activity_type || "course",
      provider: a.provider || "",
      status: a.status || "not_started",
      progress_pct: a.progress_pct ?? 0,
      priority: a.priority || "medium",
      target_date: a.target_date || "",
      hours_planned: a.hours_planned || 6,
      in_plan: !!a.in_plan,
      notes: a.notes || "",
    });
    setShowForm(true);
  };

  const deleteActivity = async (id) => {
    if (!window.confirm("Remove this learning activity?")) return;
    await axios.post(`${API_URL}/delete_learning_item.php`, {
      user_id: user.id,
      type: "activity",
      id,
    });
    await load();
    flash("Removed");
  };

  const saveGoal = async () => {
    await axios.post(`${API_URL}/save_learning_goal.php`, {
      user_id: user.id,
      title: `${goalPeriod === "monthly" ? "Monthly" : "Weekly"} learning goal`,
      period: goalPeriod,
      target_hours: Number(goalHours) || 10,
      status: "active",
    });
    await load();
    flash("Goal saved");
  };

  const saveMilestone = async () => {
    if (!milestoneTitle.trim()) return;
    await axios.post(`${API_URL}/save_learning_milestone.php`, {
      user_id: user.id,
      title: milestoneTitle.trim(),
      milestone_type: "achievement",
    });
    setMilestoneTitle("");
    await load();
    flash("Milestone added");
  };

  const saveReflection = async () => {
    if (!reflectionText.trim()) return;
    await axios.post(`${API_URL}/save_learning_reflection.php`, {
      user_id: user.id,
      content: reflectionText.trim(),
      reflection_date: new Date().toISOString().slice(0, 10),
    });
    setReflectionText("");
    await load();
    flash("Reflection saved");
  };

  const saveCert = async () => {
    const fromFile =
      certFile?.name &&
      String(certFile.name)
        .replace(/\.[^.]+$/, "")
        .replace(/[_-]+/g, " ")
        .trim();
    const title = certTitle.trim() || fromFile || "";
    if (!title) {
      alert("Please enter a certificate title (or choose a file first).");
      return;
    }
    if (!user?.id) {
      alert("Please sign in again.");
      return;
    }
    setCertBusy(true);
    try {
      const fd = new FormData();
      fd.append("user_id", String(user.id));
      fd.append("title", title);
      if (certFile) fd.append("certificate", certFile);
      const res = await axios.post(
        `${API_URL}/upload_learning_certificate.php`,
        fd
      );
      if (!res.data?.success) {
        alert(res.data?.message || "Could not save certificate.");
        return;
      }
      setCertTitle("");
      setCertFile(null);
      setCertFileKey((k) => k + 1);
      await load();
      flash("Certificate saved");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Network error while saving certificate.";
      alert(msg);
    } finally {
      setCertBusy(false);
    }
  };

  const loadRecs = async () => {
    const res = await axios.post(`${API_URL}/learning_recommendations.php`, {
      user_id: user.id,
      use_ai: true,
    });
    setRecs(res.data?.recommendations || []);
    setRecsNote(res.data?.ai_note || "");
  };

  if (!user) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        Please log in to open your learning journey.
      </div>
    );
  }

  const shell = {
    maxWidth: 720,
    margin: "0 auto",
    padding: "28px 20px 96px",
    minHeight: "100vh",
    background: "#ffffff",
    color: "#111827",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  };

  return (
    <div style={shell}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: "none",
          background: "transparent",
          color: "#6b7280",
          cursor: "pointer",
          padding: 0,
          marginBottom: 18,
          fontSize: 13,
        }}
      >
        ← Back
      </button>

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 28,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.08em",
              color: "#9ca3af",
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            COOL • LEARNER PORTAL
          </div>
          <h1
            style={{
              margin: "0 0 6px",
              fontSize: 34,
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
            }}
          >
            My Learning Journey
          </h1>
          <p style={{ margin: 0, color: "#6b7280", fontSize: 15 }}>
            Your personal learning space
          </p>
        </div>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: "#f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <GraduationCap size={26} color="#111827" strokeWidth={1.75} />
        </div>
      </div>

      {toast && (
        <div
          style={{
            marginBottom: 14,
            padding: "10px 14px",
            background: "#ecfdf5",
            border: "1px solid #a7f3d0",
            borderRadius: 12,
            color: "#065f46",
            fontSize: 14,
          }}
        >
          {toast}
        </div>
      )}
      {error && (
        <div
          style={{
            marginBottom: 14,
            padding: "10px 14px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 12,
            color: "#991b1b",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: "#6b7280", padding: "40px 0" }}>
          Loading your journey…
        </div>
      ) : (
        <>
          {/* Stats row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12,
              marginBottom: 28,
            }}
          >
            {[
              {
                icon: <BookOpen size={18} color="#111827" />,
                value: String(totals.count),
                label: "Activities",
              },
              {
                icon: <Clock size={18} color="#111827" />,
                value: `${totals.hoursDone}h`,
                label: "Hours learned",
              },
              {
                icon: <Target size={18} color="#111827" />,
                value: `${totals.overallPct}%`,
                label: "Progress",
              },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "4px 0",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {s.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      lineHeight: 1.1,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {s.value}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Overall progress */}
          <div style={{ marginBottom: 32 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 10,
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                My Learning Progress
              </div>
              <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 500 }}>
                {totals.hoursDone} / {totals.hoursGoal || "—"} hours
              </div>
            </div>
            <ProgressTrack pct={totals.overallPct} color="#22c55e" height={12} />
            <p
              style={{
                margin: "10px 0 0",
                fontSize: 13,
                color: "#9ca3af",
              }}
            >
              Keep learning to reach your next milestone!
            </p>
          </div>

          {/* Activities header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
              gap: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
              My Learning Activities
            </h2>
            <button
              type="button"
              onClick={() => {
                setForm(emptyActivity());
                setShowForm(true);
              }}
              style={{
                background: "#111827",
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "9px 16px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={15} strokeWidth={2.5} /> Add Learning
            </button>
          </div>

          {/* Add / edit modal-ish card */}
          {showForm && (
            <form
              onSubmit={saveActivity}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 18,
                marginBottom: 14,
                background: "#fafafa",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <strong>{form.id ? "Edit learning" : "Add learning"}</strong>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: 4,
                  }}
                >
                  <X size={18} />
                </button>
              </div>
              <label style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>
                Title
              </label>
              <input
                required
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="e.g. Introduction to Generative AI"
                style={{
                  width: "100%",
                  margin: "4px 0 12px",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <div>
                  <label
                    style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
                  >
                    Type
                  </label>
                  <select
                    value={form.activity_type}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        activity_type: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 4,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      fontSize: 14,
                    }}
                  >
                    {ACTIVITY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
                  >
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, status: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 4,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      fontSize: 14,
                    }}
                  >
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
                  >
                    Planned hours
                  </label>
                  <input
                    type="number"
                    min={0.5}
                    step={0.5}
                    value={form.hours_planned}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        hours_planned: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 4,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      fontSize: 14,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
                  >
                    Provider
                  </label>
                  <input
                    value={form.provider}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, provider: e.target.value }))
                    }
                    placeholder="Optional"
                    style={{
                      width: "100%",
                      marginTop: 4,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      fontSize: 14,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button
                  type="submit"
                  disabled={busyId === "form"}
                  style={{
                    background: "#111827",
                    color: "#fff",
                    border: "none",
                    borderRadius: 999,
                    padding: "10px 18px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  {busyId === "form" ? "Saving…" : "Save"}
                </button>
                {form.id && (
                  <button
                    type="button"
                    onClick={() => deleteActivity(form.id)}
                    style={{
                      background: "transparent",
                      color: "#b91c1c",
                      border: "1px solid #fecaca",
                      borderRadius: 999,
                      padding: "10px 18px",
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Activity cards */}
          {(data.activities || []).length === 0 ? (
            <div
              style={{
                border: "1px dashed #d1d5db",
                borderRadius: 16,
                padding: "36px 20px",
                textAlign: "center",
                color: "#6b7280",
                marginBottom: 20,
              }}
            >
              No learning activities yet.
              <br />
              Tap <strong>+ Add Learning</strong> to start your journey.
            </div>
          ) : (
            (data.activities || []).map((a) => {
              const { logged, planned, pct } = activityHours(a);
              const color = barColor(a.status, pct);
              const plannedLabel = planned > 0 ? planned : "—";
              return (
                <div
                  key={a.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    padding: "16px 18px 14px",
                    marginBottom: 12,
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "flex-start",
                      marginBottom: 6,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => openEdit(a)}
                      style={{
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#111827",
                        lineHeight: 1.3,
                      }}
                    >
                      {a.title}
                    </button>
                    <span
                      style={{
                        fontSize: 13,
                        whiteSpace: "nowrap",
                        ...statusStyle(a.status),
                      }}
                    >
                      {a.status_label || a.status}
                    </span>
                  </div>

                  <div
                    style={{
                      fontSize: 13,
                      color: "#6b7280",
                      marginBottom: 10,
                    }}
                  >
                    {logged} of {plannedLabel} learning hours completed
                  </div>

                  <ProgressTrack pct={pct} color={color} height={8} />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: 12,
                      gap: 10,
                    }}
                  >
                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                      {pct}% complete
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        type="button"
                        title="Remove 1 hour"
                        disabled={busyId === a.id || logged <= 0}
                        onClick={() => removeOneHour(a)}
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          border: "1px solid #e5e7eb",
                          background: "#fff",
                          cursor: logged <= 0 ? "default" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: logged <= 0 ? 0.4 : 1,
                        }}
                      >
                        <Minus size={14} />
                      </button>
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => openLogModal(a)}
                        style={{
                          border: "1px solid #111827",
                          background: "#fff",
                          color: "#111827",
                          borderRadius: 999,
                          padding: "8px 14px",
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {busyId === a.id ? "…" : "+ Log 1 hour"}
                      </button>
                    </div>
                  </div>

                  {/* Recent session notes for this activity */}
                  {(data.hours || [])
                    .filter((h) => h.activity_id === a.id && h.notes)
                    .slice(0, 2)
                    .map((h) => (
                      <div
                        key={h.id}
                        style={{
                          marginTop: 10,
                          paddingTop: 10,
                          borderTop: "1px solid #f3f4f6",
                          fontSize: 12,
                          color: "#6b7280",
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "#374151" }}>
                          {h.logged_date} · {h.hours}h
                        </span>
                        {" — "}
                        {h.notes}
                      </div>
                    ))}
                </div>
              );
            })
          )}

          {/* Log time + notes modal */}
          {logModal?.activity && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Log learning time"
              onClick={() => !busyId && setLogModal(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(17, 24, 39, 0.45)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
              }}
            >
              <form
                onSubmit={submitLogHours}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%",
                  maxWidth: 420,
                  background: "#fff",
                  borderRadius: 16,
                  padding: 20,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 12,
                    marginBottom: 14,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        color: "#9ca3af",
                        marginBottom: 4,
                      }}
                    >
                      LOG TIME
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 800 }}>
                      {logModal.activity.title}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLogModal(null)}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 4,
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>

                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6b7280",
                    marginBottom: 4,
                  }}
                >
                  Hours
                </label>
                <input
                  type="number"
                  min={0.25}
                  max={24}
                  step={0.25}
                  value={logModal.hours}
                  onChange={(e) =>
                    setLogModal((m) => ({ ...m, hours: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    fontSize: 14,
                    boxSizing: "border-box",
                    marginBottom: 12,
                  }}
                />

                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6b7280",
                    marginBottom: 4,
                  }}
                >
                  Notes{" "}
                  <span style={{ fontWeight: 400 }}>(what did you work on?)</span>
                </label>
                <textarea
                  autoFocus
                  value={logModal.notes}
                  onChange={(e) =>
                    setLogModal((m) => ({ ...m, notes: e.target.value }))
                  }
                  placeholder="e.g. Finished module 2 quiz, practiced prompts…"
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    fontSize: 14,
                    boxSizing: "border-box",
                    resize: "vertical",
                    marginBottom: 16,
                  }}
                />

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => setLogModal(null)}
                    style={{
                      border: "1px solid #e5e7eb",
                      background: "#fff",
                      borderRadius: 999,
                      padding: "10px 16px",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={busyId === logModal.activity.id}
                    style={{
                      border: "none",
                      background: "#111827",
                      color: "#fff",
                      borderRadius: 999,
                      padding: "10px 16px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {busyId === logModal.activity.id
                      ? "Saving…"
                      : "Save log"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Always-visible: Goals, Milestones, Reflections, Certificates */}
          <div style={{ marginTop: 28 }}>
            <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800 }}>
              Learning Goals
            </h2>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 16,
                marginBottom: 20,
              }}
            >
              <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>
                Set a weekly or monthly hour target for the overall progress bar.
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <select
                  value={goalPeriod}
                  onChange={(e) => setGoalPeriod(e.target.value)}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                  }}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
                <input
                  type="number"
                  min={1}
                  value={goalHours}
                  onChange={(e) => setGoalHours(e.target.value)}
                  style={{
                    width: 88,
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                  }}
                />
                <button
                  type="button"
                  onClick={saveGoal}
                  style={{
                    background: "#111827",
                    color: "#fff",
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Save goal
                </button>
              </div>
              {(data.goals || []).length === 0 ? (
                <p style={{ margin: "12px 0 0", fontSize: 13, color: "#9ca3af" }}>
                  No goals yet.
                </p>
              ) : (
                (data.goals || []).map((g) => (
                  <div
                    key={g.id}
                    style={{
                      marginTop: 12,
                      fontSize: 13,
                      color: "#374151",
                      paddingTop: 10,
                      borderTop: "1px solid #f3f4f6",
                    }}
                  >
                    <strong>{g.title}</strong> — {g.hours_done}/{g.target_hours}h
                    ({g.period}) · {g.progress_pct}%
                  </div>
                ))
              )}
            </div>

            <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800 }}>
              Milestones
            </h2>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 16,
                marginBottom: 20,
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={milestoneTitle}
                  onChange={(e) => setMilestoneTitle(e.target.value)}
                  placeholder="e.g. Finished Module 3"
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                  }}
                />
                <button
                  type="button"
                  onClick={saveMilestone}
                  style={{
                    background: "#111827",
                    color: "#fff",
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Add
                </button>
              </div>
              {(data.milestones || []).length === 0 ? (
                <p style={{ margin: "12px 0 0", fontSize: 13, color: "#9ca3af" }}>
                  No milestones yet.
                </p>
              ) : (
                (data.milestones || []).map((m) => (
                  <div
                    key={m.id}
                    style={{
                      marginTop: 10,
                      fontSize: 13,
                      borderTop: "1px solid #f3f4f6",
                      paddingTop: 10,
                    }}
                  >
                    {m.title}
                    <div style={{ color: "#9ca3af", fontSize: 12 }}>
                      {m.milestone_type}
                      {m.completed_at ? ` · ${m.completed_at}` : ""}
                    </div>
                  </div>
                ))
              )}
            </div>

            <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800 }}>
              Reflections
            </h2>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 16,
                marginBottom: 20,
              }}
            >
              <textarea
                value={reflectionText}
                onChange={(e) => setReflectionText(e.target.value)}
                placeholder="What did you learn, and how will you apply it?"
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  boxSizing: "border-box",
                  marginBottom: 8,
                }}
              />
              <button
                type="button"
                onClick={saveReflection}
                style={{
                  background: "#111827",
                  color: "#fff",
                  border: "none",
                  borderRadius: 999,
                  padding: "8px 14px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Save reflection
              </button>
              {(data.reflections || []).map((r) => (
                <div
                  key={r.id}
                  style={{
                    marginTop: 12,
                    fontSize: 13,
                    borderTop: "1px solid #f3f4f6",
                    paddingTop: 10,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {r.content}
                </div>
              ))}
            </div>

            <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800 }}>
              Certificates
            </h2>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <input
                value={certTitle}
                onChange={(e) => setCertTitle(e.target.value)}
                placeholder="Certificate title *"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  marginBottom: 8,
                  boxSizing: "border-box",
                }}
              />
              <input
                key={certFileKey}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setCertFile(file);
                  if (file && !certTitle.trim()) {
                    const base = String(file.name)
                      .replace(/\.[^.]+$/, "")
                      .replace(/[_-]+/g, " ")
                      .trim();
                    if (base) setCertTitle(base);
                  }
                }}
                style={{ marginBottom: 8, display: "block" }}
              />
              {certFile && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 8,
                  }}
                >
                  Selected: {certFile.name}
                </div>
              )}
              <button
                type="button"
                onClick={saveCert}
                disabled={certBusy}
                style={{
                  background: "#111827",
                  color: "#fff",
                  border: "none",
                  borderRadius: 999,
                  padding: "8px 14px",
                  fontWeight: 700,
                  cursor: certBusy ? "default" : "pointer",
                  display: "block",
                  opacity: certBusy ? 0.7 : 1,
                }}
              >
                {certBusy ? "Saving…" : "Save certificate"}
              </button>
              {(data.certificates || []).map((c) => (
                <div
                  key={c.id}
                  style={{
                    marginTop: 10,
                    fontSize: 13,
                    borderTop: "1px solid #f3f4f6",
                    paddingTop: 10,
                  }}
                >
                  {c.title}
                  {c.issuer ? ` · ${c.issuer}` : ""}
                  {!!c.has_file && (
                    <>
                      {" · "}
                      <a
                        href={`${API_URL}/download_learning_certificate.php?certificate_id=${c.id}&user_id=${user.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          downloadApiFile(e.currentTarget.href, {
                            fallbackName: c.title || "certificate",
                            errorMessage: "Could not download the certificate.",
                          }).catch((err) => setError(err.message));
                        }}
                      >
                        Download
                      </a>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Reminders & AI behind expand */}
          <div style={{ textAlign: "center", marginTop: 8, marginBottom: 8 }}>
            <button
              type="button"
              onClick={() => {
                const next = !showMore;
                setShowMore(next);
                if (next && moreTab === "ai" && recs.length === 0) loadRecs();
              }}
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "1px solid #e5e7eb",
                background: "#fff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title={showMore ? "Hide extras" : "Reminders & AI tips"}
            >
              <ChevronDown
                size={18}
                style={{
                  transform: showMore ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            </button>
          </div>

          {showMore && (
            <div
              style={{
                borderTop: "1px solid #f3f4f6",
                paddingTop: 18,
                marginTop: 4,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                {[
                  { id: "reminders", label: "Reminders" },
                  { id: "ai", label: "AI Tips" },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setMoreTab(t.id);
                      if (t.id === "ai") loadRecs();
                    }}
                    style={{
                      border: "none",
                      borderRadius: 999,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: moreTab === t.id ? 700 : 500,
                      background: moreTab === t.id ? "#111827" : "#f3f4f6",
                      color: moreTab === t.id ? "#fff" : "#374151",
                      cursor: "pointer",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {(moreTab === "reminders" || moreTab === "goals") && (
                <div>
                  {(data.reminders || []).length === 0 ? (
                    <p style={{ color: "#6b7280", fontSize: 13 }}>
                      You&apos;re clear — no overdue items or goal reminders.
                    </p>
                  ) : (
                    (data.reminders || []).map((r, i) => (
                      <div
                        key={`${r.title}-${i}`}
                        style={{
                          padding: "10px 0",
                          borderTop: i ? "1px solid #f3f4f6" : "none",
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 14 }}>
                          {r.title}
                        </div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>
                          {r.message}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {moreTab === "ai" && (
                <div>
                  <button
                    type="button"
                    onClick={loadRecs}
                    style={{
                      background: "#111827",
                      color: "#fff",
                      border: "none",
                      borderRadius: 999,
                      padding: "8px 14px",
                      fontWeight: 700,
                      cursor: "pointer",
                      marginBottom: 10,
                    }}
                  >
                    Refresh tips
                  </button>
                  {recsNote && (
                    <p style={{ fontSize: 12, color: "#9ca3af" }}>{recsNote}</p>
                  )}
                  {recs.map((r, i) => (
                    <div
                      key={`${r.title}-${i}`}
                      style={{
                        padding: "10px 0",
                        borderTop: "1px solid #f3f4f6",
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{r.title}</div>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>
                        {r.reason}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <Link
                  to="/learning"
                  style={{ fontSize: 13, color: "#6b7280" }}
                >
                  Open Learning Q&A →
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
